import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { 
  Plus, 
  Image as ImageIcon, 
  Video, 
  FileText, 
  Star,
  Upload,
  MoreVertical,
  Trash2,
  Download,
  Eye,
  Copy,
  Check,
  Loader2,
  ExternalLink,
  X,
  Play,
  Folder,
  Link2
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';

// Google Review Link Generator
const GOOGLE_REVIEW_BASE = "https://search.google.com/local/writereview?placeid=";
const GOOGLE_BUSINESS_SEARCH = "https://www.google.com/search?q=kite+fly+kozhikode";

export const Gallery = () => {
  const [activeTab, setActiveTab] = useState('posters');
  const [items, setItems] = useState({ posters: [], videos: [], documents: [] });
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const fileInputRef = useRef(null);
  
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    category: 'posters',
    tour_name: '',
    file: null
  });

  // Google Review Settings
  const [reviewSettings, setReviewSettings] = useState({
    place_id: '',
    business_name: 'Kites Fly - The Travel Companion',
    review_link: ''
  });

  useEffect(() => {
    loadGallery();
    loadReviewSettings();
  }, []);

  const loadGallery = async () => {
    try {
      const { data } = await api.get('/gallery');
      const grouped = {
        posters: data.filter(i => i.category === 'posters'),
        videos: data.filter(i => i.category === 'videos'),
        documents: data.filter(i => i.category === 'documents')
      };
      setItems(grouped);
    } catch (error) {
      console.error('Failed to load gallery:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadReviewSettings = async () => {
    try {
      const { data } = await api.get('/gallery/review-settings');
      if (data) {
        setReviewSettings(data);
      }
    } catch (error) {
      // Use default settings
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (max 50MB)
      if (file.size > 50 * 1024 * 1024) {
        toast.error('File size must be less than 50MB');
        return;
      }
      setUploadForm({ ...uploadForm, file, title: uploadForm.title || file.name.split('.')[0] });
    }
  };

  const handleUpload = async () => {
    if (!uploadForm.file) {
      toast.error('Please select a file');
      return;
    }
    if (!uploadForm.title) {
      toast.error('Please enter a title');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadForm.file);
      formData.append('title', uploadForm.title);
      formData.append('description', uploadForm.description);
      formData.append('category', uploadForm.category);
      formData.append('tour_name', uploadForm.tour_name);

      await api.post('/gallery/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded successfully!');
      setUploadDialogOpen(false);
      setUploadForm({ title: '', description: '', category: 'posters', tour_name: '', file: null });
      loadGallery();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await api.delete(`/gallery/${id}`);
      toast.success('Item deleted');
      loadGallery();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handlePreview = (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success('Copied to clipboard!');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy');
    }
  };

  const saveReviewSettings = async () => {
    try {
      await api.post('/gallery/review-settings', reviewSettings);
      toast.success('Review settings saved!');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const generateReviewLink = () => {
    if (reviewSettings.place_id) {
      const link = `${GOOGLE_REVIEW_BASE}${reviewSettings.place_id}`;
      setReviewSettings({ ...reviewSettings, review_link: link });
      return link;
    }
    return GOOGLE_BUSINESS_SEARCH;
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const getFileIcon = (category, mimeType) => {
    if (category === 'posters') return <ImageIcon className="w-5 h-5" />;
    if (category === 'videos') return <Video className="w-5 h-5" />;
    return <FileText className="w-5 h-5" />;
  };

  const getCategoryColor = (category) => {
    switch (category) {
      case 'posters': return 'bg-blue-100 text-blue-700';
      case 'videos': return 'bg-purple-100 text-purple-700';
      case 'documents': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div data-testid="gallery-container" className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="font-outfit text-3xl font-bold tracking-tight" style={{ color: '#84bd0e' }}>
            Gallery & Media
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage tour posters, videos, itineraries, and Google reviews
          </p>
        </div>
        <Button 
          data-testid="upload-btn"
          onClick={() => setUploadDialogOpen(true)} 
          className="text-white" 
          style={{ backgroundColor: '#383838' }}
        >
          <Upload className="w-4 h-4 mr-2" />
          Upload Media
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-slate-100">
          <TabsTrigger value="posters" className="data-[state=active]:bg-white">
            <ImageIcon className="w-4 h-4 mr-2" /> Tour Posters
            <Badge variant="secondary" className="ml-2 bg-blue-100 text-blue-700">{items.posters.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="videos" className="data-[state=active]:bg-white">
            <Video className="w-4 h-4 mr-2" /> Videos
            <Badge variant="secondary" className="ml-2 bg-purple-100 text-purple-700">{items.videos.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="documents" className="data-[state=active]:bg-white">
            <FileText className="w-4 h-4 mr-2" /> Itineraries
            <Badge variant="secondary" className="ml-2 bg-green-100 text-green-700">{items.documents.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="reviews" className="data-[state=active]:bg-white">
            <Star className="w-4 h-4 mr-2" /> Google Reviews
          </TabsTrigger>
        </TabsList>

        {/* Posters Tab */}
        <TabsContent value="posters" className="mt-6">
          {items.posters.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <ImageIcon className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Tour Posters Yet</h3>
                <p className="text-muted-foreground mb-4">Upload your first tour poster</p>
                <Button onClick={() => { setUploadForm({ ...uploadForm, category: 'posters' }); setUploadDialogOpen(true); }}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Poster
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {items.posters.map((item) => (
                <Card key={item.id} className="overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="aspect-[3/4] relative bg-slate-100">
                    {item.file_url ? (
                      <img 
                        src={item.file_url} 
                        alt={item.title} 
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <ImageIcon className="w-16 h-16 text-slate-300" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <Button size="sm" variant="secondary" onClick={() => handlePreview(item)}>
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="secondary" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm truncate">{item.title}</h4>
                    {item.tour_name && (
                      <p className="text-xs text-muted-foreground truncate">{item.tour_name}</p>
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{formatDate(item.created_at)}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Videos Tab */}
        <TabsContent value="videos" className="mt-6">
          {items.videos.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <Video className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Tour Videos Yet</h3>
                <p className="text-muted-foreground mb-4">Upload your first tour video</p>
                <Button onClick={() => { setUploadForm({ ...uploadForm, category: 'videos' }); setUploadDialogOpen(true); }}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Video
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.videos.map((item) => (
                <Card key={item.id} className="overflow-hidden group hover:shadow-lg transition-shadow">
                  <div className="aspect-video relative bg-slate-900">
                    {item.file_url ? (
                      <video 
                        src={item.file_url} 
                        className="w-full h-full object-cover"
                        muted
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Video className="w-16 h-16 text-slate-500" />
                      </div>
                    )}
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center cursor-pointer hover:bg-white transition-colors" onClick={() => handlePreview(item)}>
                        <Play className="w-8 h-8 text-[#84bd0e] ml-1" />
                      </div>
                    </div>
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button size="sm" variant="secondary" onClick={() => handleDelete(item.id)}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                  <CardContent className="p-3">
                    <h4 className="font-medium text-sm truncate">{item.title}</h4>
                    {item.tour_name && (
                      <p className="text-xs text-muted-foreground truncate">{item.tour_name}</p>
                    )}
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-muted-foreground">{formatDate(item.created_at)}</p>
                      {item.file_size && (
                        <p className="text-xs text-muted-foreground">{formatFileSize(item.file_size)}</p>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Documents/Itineraries Tab */}
        <TabsContent value="documents" className="mt-6">
          {items.documents.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-16">
                <FileText className="w-16 h-16 text-muted-foreground mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No Itineraries Uploaded Yet</h3>
                <p className="text-muted-foreground mb-4">Upload PDF itineraries for your tours</p>
                <Button onClick={() => { setUploadForm({ ...uploadForm, category: 'documents' }); setUploadDialogOpen(true); }}>
                  <Upload className="w-4 h-4 mr-2" /> Upload Itinerary
                </Button>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="divide-y">
                  {items.documents.map((item) => (
                    <div key={item.id} className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                          <FileText className="w-6 h-6 text-green-600" />
                        </div>
                        <div>
                          <h4 className="font-medium">{item.title}</h4>
                          <div className="flex items-center gap-3 text-sm text-muted-foreground">
                            {item.tour_name && <span>{item.tour_name}</span>}
                            <span>{formatDate(item.created_at)}</span>
                            {item.file_size && <span>{formatFileSize(item.file_size)}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {item.file_url && (
                          <Button variant="outline" size="sm" onClick={() => window.open(item.file_url, '_blank')}>
                            <Download className="w-4 h-4 mr-1" /> Download
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(item.id)}>
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Google Reviews Tab */}
        <TabsContent value="reviews" className="mt-6">
          <div className="grid gap-6 md:grid-cols-2">
            {/* Review Link Generator */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#84bd0e' }}>
                  <Star className="w-5 h-5 text-yellow-500" /> Google Review Link
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Business Name</Label>
                  <Input
                    value={reviewSettings.business_name}
                    onChange={(e) => setReviewSettings({ ...reviewSettings, business_name: e.target.value })}
                    placeholder="Kites Fly - The Travel Companion"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Google Place ID (Optional)</Label>
                  <Input
                    value={reviewSettings.place_id}
                    onChange={(e) => setReviewSettings({ ...reviewSettings, place_id: e.target.value })}
                    placeholder="ChIJ..."
                  />
                  <p className="text-xs text-muted-foreground">
                    Find your Place ID at <a href="https://developers.google.com/maps/documentation/places/web-service/place-id" target="_blank" rel="noopener noreferrer" className="text-[#383838] underline">Google's Place ID Finder</a>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Your Google Business Profile</Label>
                  <div className="flex gap-2">
                    <Input
                      value={GOOGLE_BUSINESS_SEARCH}
                      readOnly
                      className="bg-slate-50"
                    />
                    <Button variant="outline" onClick={() => window.open(GOOGLE_BUSINESS_SEARCH, '_blank')}>
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <Button onClick={saveReviewSettings} className="w-full" style={{ backgroundColor: '#383838' }}>
                  Save Settings
                </Button>
              </CardContent>
            </Card>

            {/* Share Review Link */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2" style={{ color: '#84bd0e' }}>
                  <Link2 className="w-5 h-5" /> Share Review Request
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-gradient-to-br from-[#84bd0e]/10 to-[#383838]/10 rounded-lg">
                  <p className="text-sm font-medium mb-3">Quick Review Link for Customers:</p>
                  <div className="flex gap-2">
                    <Input
                      value={reviewSettings.place_id ? `${GOOGLE_REVIEW_BASE}${reviewSettings.place_id}` : GOOGLE_BUSINESS_SEARCH}
                      readOnly
                      className="bg-white text-xs"
                    />
                    <Button 
                      variant="outline" 
                      onClick={() => copyToClipboard(reviewSettings.place_id ? `${GOOGLE_REVIEW_BASE}${reviewSettings.place_id}` : GOOGLE_BUSINESS_SEARCH)}
                    >
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>WhatsApp Message Template</Label>
                  <div className="relative">
                    <Textarea
                      readOnly
                      className="bg-slate-50 text-sm pr-12"
                      rows={5}
                      value={`🙏 Dear Valued Customer,

Thank you for traveling with *Kites Fly*! We hope you had an amazing experience.

Would you mind leaving us a quick review? It helps others discover our services.

⭐ Leave a Review: ${reviewSettings.place_id ? `${GOOGLE_REVIEW_BASE}${reviewSettings.place_id}` : GOOGLE_BUSINESS_SEARCH}

Thank you for your support!
🦅 *Kites Fly* - The Travel Companion`}
                    />
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="absolute top-2 right-2"
                      onClick={() => copyToClipboard(`🙏 Dear Valued Customer,

Thank you for traveling with *Kites Fly*! We hope you had an amazing experience.

Would you mind leaving us a quick review? It helps others discover our services.

⭐ Leave a Review: ${reviewSettings.place_id ? `${GOOGLE_REVIEW_BASE}${reviewSettings.place_id}` : GOOGLE_BUSINESS_SEARCH}

Thank you for your support!
🦅 *Kites Fly* - The Travel Companion`)}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(`Please leave us a review: ${reviewSettings.place_id ? `${GOOGLE_REVIEW_BASE}${reviewSettings.place_id}` : GOOGLE_BUSINESS_SEARCH}`)}`, '_blank')}>
                    Share on WhatsApp
                  </Button>
                  <Button variant="outline" onClick={() => window.open(GOOGLE_BUSINESS_SEARCH, '_blank')}>
                    View Business Profile
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-outfit" style={{ color: '#84bd0e' }}>
              Upload Media
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <div className="grid grid-cols-3 gap-2">
                {['posters', 'videos', 'documents'].map((cat) => (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => setUploadForm({ ...uploadForm, category: cat })}
                    className={`p-3 rounded-lg border-2 transition-all capitalize ${uploadForm.category === cat ? 'border-[#383838] bg-[#383838]/10' : 'border-gray-200'}`}
                  >
                    {cat === 'posters' && <ImageIcon className="w-5 h-5 mx-auto mb-1" />}
                    {cat === 'videos' && <Video className="w-5 h-5 mx-auto mb-1" />}
                    {cat === 'documents' && <FileText className="w-5 h-5 mx-auto mb-1" />}
                    <div className="text-xs">{cat === 'documents' ? 'Itinerary' : cat}</div>
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Select File</Label>
              <div 
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-[#383838] transition-colors"
                onClick={() => fileInputRef.current?.click()}
              >
                {uploadForm.file ? (
                  <div className="flex items-center justify-center gap-3">
                    {getFileIcon(uploadForm.category)}
                    <div className="text-left">
                      <p className="font-medium text-sm">{uploadForm.file.name}</p>
                      <p className="text-xs text-muted-foreground">{formatFileSize(uploadForm.file.size)}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setUploadForm({ ...uploadForm, file: null }); }}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                    <p className="text-sm text-muted-foreground">Click to select a file</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {uploadForm.category === 'posters' && 'JPG, PNG, WebP (max 50MB)'}
                      {uploadForm.category === 'videos' && 'MP4, MOV, AVI (max 50MB)'}
                      {uploadForm.category === 'documents' && 'PDF, DOC, DOCX (max 50MB)'}
                    </p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={
                  uploadForm.category === 'posters' ? 'image/*' :
                  uploadForm.category === 'videos' ? 'video/*' :
                  '.pdf,.doc,.docx'
                }
                onChange={handleFileSelect}
              />
            </div>

            <div className="space-y-2">
              <Label>Title *</Label>
              <Input
                value={uploadForm.title}
                onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
                placeholder="e.g., Goa Beach Tour Poster"
              />
            </div>

            <div className="space-y-2">
              <Label>Tour Name (Optional)</Label>
              <Input
                value={uploadForm.tour_name}
                onChange={(e) => setUploadForm({ ...uploadForm, tour_name: e.target.value })}
                placeholder="e.g., Goa Beach Getaway"
              />
            </div>

            <div className="space-y-2">
              <Label>Description (Optional)</Label>
              <Textarea
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Brief description..."
                rows={2}
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
              <Button 
                onClick={handleUpload} 
                disabled={uploading || !uploadForm.file}
                className="text-white"
                style={{ backgroundColor: '#383838' }}
              >
                {uploading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Upload
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="font-outfit" style={{ color: '#84bd0e' }}>
              {previewItem?.title}
            </DialogTitle>
          </DialogHeader>
          {previewItem && (
            <div className="mt-4">
              {previewItem.category === 'posters' && previewItem.file_url && (
                <img src={previewItem.file_url} alt={previewItem.title} className="max-w-full max-h-[70vh] mx-auto rounded-lg" />
              )}
              {previewItem.category === 'videos' && previewItem.file_url && (
                <video src={previewItem.file_url} controls className="max-w-full max-h-[70vh] mx-auto rounded-lg" />
              )}
              {previewItem.description && (
                <p className="text-sm text-muted-foreground mt-4">{previewItem.description}</p>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
